import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRightIcon,
  XMarkIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';
import { track, ACTIVATION_EVENTS } from '@/lib/analytics';

/**
 * Compact getting-started progress row shown on the team Overview until
 * either (a) all required steps are complete or (b) the user dismisses it.
 *
 * 2026-07-23: slimmed from a full 4-step ladder to a one-line progress row.
 * The Home dashboard owns the rich checklist (GettingStartedChecklist in
 * NextStepCard.jsx, per features/coach-getting-started/) — showing the same
 * four steps in two places made onboarding feel like eight steps. This row
 * keeps the team page aware of setup progress and hands off to Home for
 * the guided version.
 *
 * Step definitions stay aligned with the Home checklist and the admin
 * stage ladder: team → season → registration (NOT roster presence — the
 * dark-bridge trap) → first message.
 *
 * Props:
 *  - hasTeam, hasSeason, hasRoster, hasRegistration, hasMessage (booleans)
 *  - hasInvite, teamId — accepted for caller compatibility (unused here)
 *  - onDismiss () => void
 */
export default function OnboardingChecklist({
  hasTeam,
  hasRoster,
  hasSeason,
  hasInvite,          // eslint-disable-line no-unused-vars
  hasRegistration,
  hasMessage = false,
  teamId,             // eslint-disable-line no-unused-vars
  onDismiss,
}) {
  const { t } = useTranslation();

  // Step 3's proxy is a completed REGISTRATION, not roster presence — a
  // hand-typed roster reaches nobody while the roster bridge is dark.
  const regDone = hasRegistration !== undefined ? hasRegistration : hasRoster;
  const doneFlags = [hasTeam, hasSeason, regDone, hasMessage];
  const completedCount = doneFlags.filter(Boolean).length;
  const total = doneFlags.length;

  // Hide once all required steps are done.
  if (completedCount === total) return null;

  const pct = Math.round((completedCount / total) * 100);

  const handleDismiss = () => {
    track(ACTIVATION_EVENTS.CHECKLIST_DISMISSED, {
      completed_count: completedCount,
      total,
    });
    onDismiss?.();
  };

  const handleContinue = () => {
    track(ACTIVATION_EVENTS.CHECKLIST_ITEM_CLICKED, {
      item_id: 'compact_continue',
      completed_count: completedCount,
    });
  };

  return (
    <div className="mb-5 flex items-center gap-3 rounded-2xl border border-[#b5f03d]/30 bg-gradient-to-r from-[#b5f03d]/10 to-white dark:from-[#b5f03d]/10 dark:to-zinc-900 px-4 py-3 shadow-sm">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[#b5f03d]/20">
        <RocketLaunchIcon className="h-4 w-4 text-[#0d1f14] dark:text-[#b5f03d]" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <p
            className="uppercase text-zinc-900 dark:text-white truncate"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 14, letterSpacing: '0.5px' }}
          >
            {t('dashboard.checklist.title')}
          </p>
          <span
            className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-[#0d1f14] dark:text-[#b5f03d]"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.8px' }}
          >
            {t('dashboard.checklist.compactProgress', { done: completedCount, total })}
          </span>
        </div>
        <div className="mt-1.5 h-1.5 w-full rounded-full bg-[#b5f03d]/15 overflow-hidden">
          <div
            className="h-full rounded-full bg-[#b5f03d] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <Link
        to="/"
        onClick={handleContinue}
        className="shrink-0 inline-flex items-center gap-1 rounded-[10px] bg-[#b5f03d] hover:bg-[#94cc28] px-3 py-1.5 text-xs font-bold uppercase text-[#0d1f14] transition-colors"
        style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: '0.6px' }}
      >
        {t('dashboard.checklist.continue')}
        <ArrowRightIcon className="h-3.5 w-3.5" />
      </Link>

      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('dashboard.checklist.dismissAria')}
        className="shrink-0 rounded-lg p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-200 transition-colors"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
